import { navigate } from '@reach/router'
import { Input, Row, Button, Form, Col, Alert } from 'antd'
import moment from 'moment'
import React, { createContext, useContext, useReducer, useState } from 'react'
import styled from 'styled-components'
import { ROOT_PATH } from '../common/config'
import { encryptPhone } from '../common/helper'
import { useLoading, useSMS } from '../common/hooks'
import { required, validateMessages } from '../common/validate'
import { forgotUserSendSMS, forgotUserSMSVerify, nricValidation } from '../services'
import { ErrorBanner } from './ui'

const StyledForgotForm = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;

  form {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-form-item {
    width: 60%;
  }

  .button-groups {
    margin-top: auto;
    align-self: flex-end;
    width: 100%;
    margin-bottom: 78px;

    .ant-btn {
      width: 120px !important;
    }
  }

  h1 p {
    font-weight: normal;
    margin-top: 1em;
    font-size: 16px;
    margin-bottom: -1em;
  }
`

enum Step {
  NRIC,
  OTP,
  RESET,
}

interface ForgotState {
  step: Step
  nric: string
  // 忘记账号请求流水号
  fuId: string
  username: string
}

type ForgotAction = { type: 'ChangeStep'; payload: Step } | { type: 'ApplyForm'; payload: Partial<ForgotState> }
export const ForgotContext = createContext({ state: {} as ForgotState, dispatch: {} as React.Dispatch<ForgotAction> })

const useForgot = () => {
  const initialState: ForgotState = {
    step: Step.NRIC,
    nric: '',
    // 忘记账号请求流水号
    fuId: '',
    username: '',
  }
  const reducer = (state: ForgotState, action: ForgotAction): ForgotState => {
    switch (action.type) {
      case 'ChangeStep':
        return { ...state, step: action.payload }
      case 'ApplyForm':
        return { ...state, ...action.payload }
      default:
        return state
    }
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  return {
    state,
    dispatch,
  }
}
export const ForgotProvider: React.FC = (props) => {
  const { state, dispatch } = useForgot()
  return <ForgotContext.Provider value={{ state, dispatch }}>{props.children}</ForgotContext.Provider>
}

const ButtonGroup: React.FC<{ loading?: boolean; last?: boolean; errMsg?: string }> = (props) => {
  const { state, dispatch } = useContext(ForgotContext)
  const back = () => {
    if (state.step === Step.NRIC) navigate(ROOT_PATH)
    else dispatch({ type: 'ChangeStep', payload: state.step - 1 })
  }

  return (
    <>
      <Row align="middle" justify="space-between" className="button-groups">
        {!props.last && (
          <Button size="large" onClick={back}>
            Back
          </Button>
        )}
        <Button size="large" type="primary" htmlType="submit" loading={props.loading}>
          {props.last ? 'Return' : 'Continue'}
        </Button>
      </Row>
      {props.errMsg && <ErrorBanner>{props.errMsg}</ErrorBanner>}
    </>
  )
}

const NRIC: React.FC = () => {
  const { dispatch } = useContext(ForgotContext)
  const { loading, start, finish } = useLoading()
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined)

  const onFinish = async (values: { nric: string }) => {
    console.log(values)
    start()
    setErrMsg(undefined)
    try {
      await nricValidation({ nric: values.nric })
      dispatch({ type: 'ApplyForm', payload: { nric: values.nric } })
      dispatch({ type: 'ChangeStep', payload: Step.OTP })
      finish()
      setErrMsg(undefined)
    } catch (error) {
      setErrMsg(error.message)
      finish()
    }
  }
  return (
    <>
      <Form layout="vertical" requiredMark={false} validateMessages={{ required: 'please enter valid NRIC no.' }} onFinish={onFinish}>
        <h1>Forgotten Username</h1>
        <Form.Item label="Enter your NRIC No" name="nric" rules={[required]}>
          <Input size="large" />
        </Form.Item>
        <ButtonGroup loading={loading} errMsg={errMsg} />
      </Form>
    </>
  )
}

const OTP: React.FC = () => {
  const { dispatch, state } = useContext(ForgotContext)
  const { loading, start, finish } = useLoading()
  const [tel, setTel] = useState('')
  const { sending, sendSMS, remaining, smsSended } = useSMS(
    () => forgotUserSendSMS({ nric: state.nric }),
    ({ fuId, tel }) => {
      setTel(tel)
      dispatch({ type: 'ApplyForm', payload: { fuId } })
    }
  )
  const onFinish = async (values: { sms: string }) => {
    start()
    try {
      const { userId } = await forgotUserSMSVerify({ sms: values.sms, fuId: state.fuId })
      finish()
      dispatch({ type: 'ApplyForm', payload: { username: userId } })
      dispatch({ type: 'ChangeStep', payload: Step.RESET })
    } catch (error) {
      finish()
    }
  }
  return (
    <>
      <Form layout="vertical" requiredMark={false} validateMessages={{ required: 'please enter your NRIC no.' }} onFinish={onFinish}>
        <h1>Forgotten Username</h1>
        <Form.Item
          label="Enter Your One Time Password"
          name="sms"
          rules={[{ ...required, message: 'please enter your OTP' }]}
          extra={tel ? 'OTP has been sent to your phone: ' + encryptPhone(tel) : undefined}
        >
          <Row align="middle">
            <Col span="18">
              <Input size="large" />
            </Col>
            <Col span="6">
              <Button type="link" loading={sending} onClick={sendSMS}>
                {smsSended ? `remaining ${moment.utc(remaining * 60 * 1000).format('HH:mm')}` : 'Send OTP'}
              </Button>
            </Col>
          </Row>
        </Form.Item>
        <ButtonGroup loading={loading} />
      </Form>
    </>
  )
}
const RESET: React.FC = () => {
  const { state } = useContext(ForgotContext)
  // const { loading, start, finish } = useLoading()

  const onFinish = async () => {
    // try {
    //   start()
    //   await forgotUserUpdate({ rnpwd: password, npwd: password, fuId: state.fuId })
    //   finish()
    //   Modal.success({
    //     maskClosable: false,
    //     title: 'Success',
    //     content: 'Your have successfully reset!',
    //     onOk: () => navigate('/'),
    //   })
    // } catch (error) {
    //   finish()
    // }
    navigate(ROOT_PATH)
  }
  return (
    <>
      <Form layout="vertical" requiredMark={false} validateMessages={validateMessages} onFinish={onFinish}>
        <h1>Forgotten Username</h1>
        <p className="text-lg"> Your Username is </p>
        <Alert message={state.username} type="info" className="text-2xl" />

        {/* <h3>Reset Password </h3>
        <p>You can now reset your new password.</p>
        <Form.Item name="password" label="Enter Your New Password" rules={[{ ...passwordRule, message: 'your new password is not valid' }]}>
          <Row align="middle" gutter={1} justify="space-between">
            <Col span={20}>
              <Input.Password size="large" type="password" />
            </Col>
            <Col span={3}>
              <Tooltip title="Your password should be at least 8 alphanumeric character. It should contain at least one alphabet and one digit.">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
              </Tooltip>
            </Col>
          </Row>
        </Form.Item>
        <Form.Item
          name="password2"
          label="Reenter Your New Password"
          rules={[
            { ...passwordRule, message: 'your new password is not valid' },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve()
                }
                return Promise.reject('The two passwords that you entered do not match!')
              },
              validateTrigger: 'blur',
            }),
          ]}
        >
          <Row align="middle" gutter={1} justify="space-between">
            <Col span={20}>
              <Input.Password size="large" type="password" />
            </Col>
            <Col span={3}>
              {
            </Col>
          </Row>
        </Form.Item>
         */}
        <ButtonGroup last />
      </Form>
    </>
  )
}

const Steps = {
  [Step.NRIC]: NRIC,
  [Step.OTP]: OTP,
  [Step.RESET]: RESET,
}

export const ForgotUser = () => {
  const { state } = useContext(ForgotContext)
  const StepForm = Steps[state.step]
  return (
    <StyledForgotForm>
      <StepForm />
    </StyledForgotForm>
  )
}
